/**
 * clasificadores/tendencias.js — Lógica del dashboard de Tendencias (ADR 036).
 *
 * Función PURA: cruza las publicaciones del seller con las tendencias de demanda
 * (refTendencias) y devuelve las secciones accionables SIN IA + KPIs. No toca red
 * ni Firestore. Los chips con IA (título/clip) y la posición (highlights) NO viven
 * acá — son tajadas 3-4.
 *
 * Read-only de cabo a rabo: cada accionable es una SUGERENCIA; el seller la aplica
 * en ML (deep-link). Nosotros nunca escribimos en ML.
 *
 * Input:
 *   - publicaciones          : array canónico (núcleo + datosCanal). Lee con
 *                              fallbacks: categoryId, stock, health.score, catalogo.status.
 *   - tendenciasPorCategoria : { [categoryId]: { keywords: [{keyword,url}] } }  (refTendencias por cat)
 *   - tendenciasPais         : { keywords: [{keyword,url}] }                     (refTendencias país / CAT_PAIS)
 *   - adsItems               : array de ads canónicos (status, item_id) → "tiene ad vivo"
 *   - umbrales (opcional)    : { umbralCalidad, statusPerdiendo[] }
 *
 * Output:
 *   {
 *     secciones: {
 *       sinStock          : [],  // categoría caliente + stock 0
 *       sinAds            : [],  // categoría caliente + sin ad vivo
 *       bajaCalidad       : [],  // categoría caliente + health.score < umbral
 *       perdiendoBuyBox   : [],  // categoría caliente + catalogo.status perdiendo
 *       gapDeProducto     : [],  // keyword top del país sin ninguna pub que la cubra
 *     },
 *     analisis: { keywordsEnTendencia, publicacionesImpactables, oportunidadesSinCobertura, categoriasCalientes },
 *   }
 */

import { esAdVivo } from '@modules/mercadolibre/lib/promoCanonicoShape'

const DEFAULTS = {
  umbralCalidad: 70,                 // health.score < umbral → "baja calidad" (escala 0-100)
  statusPerdiendo: ['competing'],    // catalogo.status que cuenta como perdiendo el buy-box
}

// Normaliza texto para el match de keywords (minúsculas, sin acentos, trim).
function norm(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim()
}

/**
 * Categorías distintas que el seller realmente usa (para pedirle tendencias a
 * `resolverTendencias`). Capea en `max` (el callable acepta hasta 50 por llamada).
 * Devuelve los categoryId más frecuentes primero (prioriza donde hay más catálogo).
 */
export function categoriasDistintas(publicaciones = [], { max = 50 } = {}) {
  const conteo = new Map()
  for (const it of publicaciones) {
    const cat = it.categoryId ?? it.datosCanal?.categoryId ?? null
    if (!cat) continue
    conteo.set(cat, (conteo.get(cat) || 0) + 1)
  }
  return [...conteo.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([cat]) => cat)
}

/**
 * Evolución del ranking desde el histórico semanal (`refTendencias.historial`,
 * cada snapshot = { semana, keywords: [string ordenado] }). Devuelve data lista
 * para recharts + los deltas de la última semana vs. la anterior.
 *
 * @returns {{ data: Array, keywords: string[], deltas: Object }}
 *   data: [{ semana, [keyword]: puesto|null }, ...] (una fila por semana)
 *   keywords: las topN del último snapshot (una <Line> por cada una)
 *   deltas: { [keyword]: { estado: 'nuevo'|'subio'|'bajo'|'igual', n? } }
 */
export function computarEvolucionRanking(historial = [], { topN = 6, maxSemanas = 8 } = {}) {
  const hist = (Array.isArray(historial) ? historial : []).slice(-maxSemanas)
  if (hist.length === 0) return { data: [], keywords: [], deltas: {} }
  const ultima = hist[hist.length - 1]
  const previa = hist.length > 1 ? hist[hist.length - 2] : null
  const rankEn = (snap, kw) => { const i = (snap?.keywords || []).indexOf(kw); return i >= 0 ? i + 1 : null }
  const keywords = (ultima.keywords || []).slice(0, topN)
  const data = hist.map(s => {
    const row = { semana: s.semana }
    for (const kw of keywords) row[kw] = rankEn(s, kw)
    return row
  })
  const deltas = {}
  for (const kw of (ultima.keywords || [])) {
    const ahora = rankEn(ultima, kw)
    const antes = previa ? rankEn(previa, kw) : null
    if (antes == null) deltas[kw] = { estado: 'nuevo' }
    else if (ahora < antes) deltas[kw] = { estado: 'subio', n: antes - ahora }
    else if (ahora > antes) deltas[kw] = { estado: 'bajo', n: ahora - antes }
    else deltas[kw] = { estado: 'igual' }
  }
  return { data, keywords, deltas }
}

// Palabras significativas de un texto (para concordancia AMPLIA): ≥4 letras,
// plural aproximado a singular (banda↔bandas), sin stopwords.
const STOP = new Set(['para', 'con', 'sin', 'los', 'las', 'del', 'por', 'una', 'uno', 'que', 'mas'])
function tokensSig(s) {
  return norm(s).split(/[^a-z0-9]+/)
    .map(t => t.replace(/(es|s)$/, ''))
    .filter(t => t.length >= 4 && !STOP.has(t))
}

// Spans [inicio, fin) sobre el título ORIGINAL que matchean la keyword, para
// resaltarlos en la UI. exacta = el tramo literal (expandido a palabra entera);
// amplia = las palabras del título cuyo stem coincide con un token de la keyword.
// Devuelve spans mergeados y ordenados. Pensado para concordancia exacta/amplia.
export function spansResaltado(titulo, kwMatch, tier) {
  const orig = String(titulo || '')
  if (!orig || !kwMatch || (tier !== 'exacta' && tier !== 'amplia')) return []

  // Mapa char-a-char norm(SIN trim) → índice original (norm puede cambiar largo
  // por NFD, así que mapeamos cada char en vez de asumir 1:1).
  let normStr = ''
  const map = []
  for (let i = 0; i < orig.length; i++) {
    const nc = orig[i].toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    for (let j = 0; j < nc.length; j++) { normStr += nc[j]; map.push(i) }
  }
  const esAlnum = (ch) => /[\p{L}\p{N}]/u.test(ch)
  const expandirAPalabra = (a, b) => {
    let s = a, e = b
    while (s > 0 && esAlnum(orig[s - 1])) s--
    while (e < orig.length && esAlnum(orig[e])) e++
    return [s, e]
  }

  const spans = []
  if (tier === 'exacta') {
    const needle = norm(kwMatch)
    const idx = needle ? normStr.indexOf(needle) : -1
    if (idx >= 0) spans.push(expandirAPalabra(map[idx], map[idx + needle.length - 1] + 1))
  }
  // amplia, o exacta sin match literal (fallback): palabras por stem.
  if (spans.length === 0) {
    const kwToks = new Set(tokensSig(kwMatch))
    const re = /[\p{L}\p{N}]+/gu
    let m
    while ((m = re.exec(orig)) !== null) {
      const stem = norm(m[0]).replace(/(es|s)$/, '')
      if (stem.length >= 4 && kwToks.has(stem)) spans.push([m.index, m.index + m[0].length])
    }
  }

  spans.sort((x, y) => x[0] - y[0])
  const merged = []
  for (const sp of spans) {
    const last = merged[merged.length - 1]
    if (last && sp[0] <= last[1]) last[1] = Math.max(last[1], sp[1])
    else merged.push([sp[0], sp[1]])
  }
  return merged
}

// Nivel de demanda de una keyword = su posición dentro de su categoría, en
// tercios. Una sola definición: la usan la cobertura y el orden de las
// secciones de título.
const NIVEL_RANK = { alto: 0, medio: 1, bajo: 2 }
function nivelDeKeyword(i, n) {
  const frac = n <= 1 ? 0 : i / (n - 1)
  return frac < 1 / 3 ? 'alto' : frac <= 2 / 3 ? 'medio' : 'bajo'
}
// Título amplia: lo más fuerte primero (la cuota del lote recorta el final,
// `agentes.js:1064`), por nivel de la keyword y, a igual nivel, precio. Sin nivel, al final.
const rankDeNivel = (nivel) => NIVEL_RANK[nivel] ?? 3
const porNivelYPrecio = (a, b) => rankDeNivel(a._nivelKw) - rankDeNivel(b._nivelKw) || (b._precio || 0) - (a._precio || 0)

// Lectura tolerante al shape (núcleo top-level o dentro de datosCanal).
function leerPub(it) {
  const dc = it.datosCanal || {}
  return {
    itemId: String(it.itemId ?? it.id ?? ''),
    titulo: it.title || it.titulo || String(it.itemId ?? it.id ?? ''),
    permalink: it.permalink || dc.permalink || null,
    categoryId: it.categoryId ?? dc.categoryId ?? null,
    stock: Number(it.stockDisponible ?? it.availableQuantity ?? it.available_quantity ?? dc.stockDisponible ?? 0),
    healthScore: it.health?.score ?? dc.health?.score ?? null,
    buyboxStatus: it.catalogo?.status ?? dc.catalogo?.status ?? null,
    precio: it.precio ?? it.price ?? null,
    moneda: it.moneda ?? it.currency_id ?? null,
    imagen: it.imagenPrincipal ?? it.thumbnail ?? dc.imagenPrincipal ?? null,
    urlPublica: it.urlPublica ?? it.permalink ?? dc.permalink ?? null,
    estado: it.estado ?? null,
    vendidas: Number(it.soldQuantity ?? dc.soldQuantity ?? 0),
  }
}

export function clasificarTendencias({
  publicaciones = [],
  tendenciasPorCategoria = {},
  tendenciasPais = { keywords: [] },
  adsItems = [],
  umbrales = {},
}) {
  const umbralCalidad = Number(umbrales.umbralCalidad ?? DEFAULTS.umbralCalidad)
  const statusPerdiendo = umbrales.statusPerdiendo || DEFAULTS.statusPerdiendo

  // Categorías "calientes" = las que tienen tendencias con keywords.
  const calientes = new Map() // categoryId → keywords[]
  for (const [catId, data] of Object.entries(tendenciasPorCategoria)) {
    const kws = Array.isArray(data?.keywords) ? data.keywords : []
    if (kws.length > 0) calientes.set(catId, kws)
  }

  // Union de keywords de TUS categorías (norm) — base de "relevancia". Una keyword
  // del país es relevante si matchea (contiene/contenida) alguna de tus categorías.
  const catKwNorm = []
  for (const kws of calientes.values()) for (const kw of kws) { const k = norm(kw?.keyword); if (k) catKwNorm.push(k) }
  const esRelevante = (k) => catKwNorm.some(ck => ck === k || ck.includes(k) || k.includes(ck))

  // Ads vivos por itemId (status intradía-volátil → esAdVivo, NO status==='active').
  const itemIdsConAd = new Set()
  for (const ad of adsItems) {
    if (!esAdVivo(ad.status)) continue
    const itemId = ad.item_id || (ad.id ? String(ad.id).split('_').pop() : null)
    if (itemId) itemIdsConAd.add(String(itemId))
  }

  const paisKw = tendenciasPais.keywords || []

  // Concordancia POR PUBLICACIÓN vs las keywords de SU categoría:
  //   exacta = el título contiene una keyword literal · amplia = comparte ≥1
  //   palabra significativa · sin = ninguna (pero la categoría SÍ tiene tendencias →
  //   hay keywords para sugerir). Los chips cruzan concordancia × condición.
  const pubsConcordancia = { exacta: [], amplia: [], sin: [] }
  const secciones = { exactaSinStock: [], exactaBuyBox: [], ampliaOptimizar: [], sinConcordancia: [] }

  for (const raw of publicaciones) {
    const p = leerPub(raw)
    if (!p.categoryId || !calientes.has(p.categoryId)) continue // solo categorías con tendencias

    const catKws = calientes.get(p.categoryId)
    const tnorm = norm(p.titulo)
    const tToks = new Set(tokensSig(p.titulo))

    let tier = 'sin'
    let kwMatch = null
    for (const kw of catKws) { const k = norm(kw?.keyword); if (k && tnorm.includes(k)) { tier = 'exacta'; kwMatch = kw.keyword; break } }
    if (tier === 'sin') {
      for (const kw of catKws) { if (tokensSig(kw?.keyword).some(t => tToks.has(t))) { tier = 'amplia'; kwMatch = kw.keyword; break } }
    }

    const idxMatch = kwMatch == null ? -1 : catKws.findIndex(k => k?.keyword === kwMatch)
    const sinVentas = p.vendidas === 0
    const perdiendoBB = !!(p.buyboxStatus && statusPerdiendo.includes(p.buyboxStatus))
    const row = {
      id: p.itemId, _itemId: p.itemId, _titulo: p.titulo, _precio: p.precio, _moneda: p.moneda,
      _imagen: p.imagen, _estado: p.estado, _stock: p.stock, _vendidas: p.vendidas, _url: p.urlPublica,
      _categoryId: p.categoryId, _tier: tier, _kwMatch: kwMatch,
      _resaltar: spansResaltado(p.titulo, kwMatch, tier),
      _nivelKw: idxMatch >= 0 ? nivelDeKeyword(idxMatch, catKws.length) : null,
      _sugeridas: tier === 'sin' ? catKws.slice(0, 4).map(k => k.keyword) : null,
    }
    pubsConcordancia[tier].push(row)

    if (tier === 'exacta' && p.stock <= 0) secciones.exactaSinStock.push(row)
    if (tier === 'exacta' && perdiendoBB) secciones.exactaBuyBox.push(row)
    if (tier === 'amplia' && sinVentas) secciones.ampliaOptimizar.push(row)
    if (tier === 'sin' && sinVentas) secciones.sinConcordancia.push(row)
  }

  const publicacionesImpactables = pubsConcordancia.exacta.length + pubsConcordancia.amplia.length

  // Cobertura por título (compartido por ranking, gap y matriz).
  const titulosNorm = publicaciones.map(it => norm(leerPub(it).titulo))
  const cubreKeyword = (k) => titulosNorm.some(t => t.includes(k))

  // Ranking general del país (3 bloques posicionales que ML ya ordena: 0-9
  // creciendo, 10-29 más deseadas, 30-49 populares). Dot: verde cubierta · rojo
  // gap PERO relevante a tus categorías (oportunidad real) · gris gap no-relevante.
  const gapDeProducto = []
  const rankingPais = []
  paisKw.forEach((kw, i) => {
    const k = norm(kw.keyword)
    if (!k) return
    const bloque = i < 10 ? 'creciendo' : i < 30 ? 'deseadas' : 'populares'
    const cubierta = cubreKeyword(k)
    const entry = { id: `${i}_${k}`, _keyword: kw.keyword, _url: kw.url || null, _bloque: bloque, _posicion: i + 1, _cubierta: cubierta, _relevante: esRelevante(k) }
    rankingPais.push(entry)
    if (!cubierta) gapDeProducto.push(entry)
  })

  // Matriz demanda × cobertura — SOLO keywords de TUS categorías (las del país
  // tipo "figuritas mundial" no van: no tienen que ver con lo que vende). Demanda
  // = posición dentro de su categoría (mitad superior = alta). Dedup por keyword.
  // Cobertura por CONCORDANCIA (modelo tipo Ads), solo keywords de TUS categorías:
  //   exacta  = el título contiene la búsqueda literal.
  //   amplia  = comparte ≥1 palabra significativa (vendés algo relacionado, el
  //             título no lo dice igual → optimizá el título).
  //   noCubre = ni relación (oportunidad de producto nuevo).
  // Nivel (alto/medio/bajo) = posición de la keyword dentro de su categoría.
  const pubTokens = publicaciones.map(it => new Set(tokensSig(leerPub(it).titulo)))
  const cobertura = { exacta: [], amplia: [], noCubre: [] }
  const cobVistas = new Set()
  for (const [catId, kws] of calientes) {
    const n = kws.length
    kws.forEach((kw, i) => {
      const k = norm(kw?.keyword)
      if (!k || cobVistas.has(k)) return
      cobVistas.add(k)
      const nivel = nivelDeKeyword(i, n)
      const kwToks = tokensSig(kw.keyword)
      const tier = cubreKeyword(k) ? 'exacta'
        : (pubTokens.some(set => kwToks.some(t => set.has(t))) ? 'amplia' : 'noCubre')
      cobertura[tier].push({ id: `${catId}_${k}`, _keyword: kw.keyword, _url: kw.url || null, _categoryId: catId, _nivel: nivel, _tier: tier })
    })
  }
  for (const tier of Object.keys(cobertura)) cobertura[tier].sort((a, b) => NIVEL_RANK[a._nivel] - NIVEL_RANK[b._nivel])

  // KPIs.
  let keywordsEnTendencia = 0
  for (const kws of calientes.values()) keywordsEnTendencia += kws.length
  const cubiertasPais = rankingPais.filter(e => e._cubierta).length

  return {
    secciones,         // chips: { exactaSinStock, exactaBuyBox, ampliaOptimizar, sinConcordancia }
    pubsConcordancia,  // { exacta, amplia, sin } — todas las pubs por concordancia (filtro por card)
    gapDeProducto,     // keywords país sin pub (para el KPI de oportunidades)
    cobertura,         // { exacta, amplia, noCubre } — keywords de tus categorías por concordancia
    rankingPais,       // las keywords del país con bloque + posición + cobertura
    analisis: {
      keywordsEnTendencia,
      publicacionesImpactables,
      oportunidadesSinCobertura: gapDeProducto.length,
      categoriasCalientes: calientes.size,
      coberturaPais: { cubiertas: cubiertasPais, total: rankingPais.length },
    },
  }
}
// ═══════════════════════════════════════════════════════════════════════════
// 🎯 Detección de tareas (12-jul-2026) — secciones con shape de tarea.
// ───────────────────────────────────────────────────────────────────────────
// Cruza las MISMAS secciones del dashboard (clasificarTendencias) con:
//   • keywordsExcluidas (empresa.tendenciasConfig) — curación manual: lo
//     excluido no existe para el motor (declarado con contador).
//   • marcas propias (colección marcas) — una keyword que contiene una marca
//     propia es compatible por definición (sin IA).
//   • veredictos IA por par título×keyword (tendenciasVeredictosTitulo, del
//     callable sugerirTendenciaIA tipo 'compatibilidad'): las sugerencias de
//     TÍTULO (amplia/sin) SOLO generan tarea si el par está VERIFICADO
//     compatible (aplica=true). Sin veredicto → pendienteIA (declarado — la
//     vista ofrece "Verificar con IA"); aplica=false → vetada (marca ajena /
//     atributo que no aplica: "cafe martinez", "corpiño lactancia").
//   • Las secciones EXACTAS (reponer / buy-box) NO pasan por el gate: el
//     título YA contiene la búsqueda — no se sugiere agregar nada.
//
// Secciones → tipo de tarea:
//   reponer_tendencia → reponer_stock · buybox_tendencia → revisar_competitividad
//   titulo_amplia / titulo_sin → modificar_publicacion (comparten cuota).
// gapDeProducto queda DECLARADO (decisión de sourcing, no tarea de operador).
// ═══════════════════════════════════════════════════════════════════════════

export const SECCIONES_TENDENCIAS_TAREAS = ['reponer_tendencia', 'buybox_tendencia', 'titulo_amplia', 'titulo_sin']

export function clasificarTendenciasTareas({
  publicacionesTendencias = null,
  publicaciones = [],
  tendenciasPorCategoria = {},
  tendenciasPais = { keywords: [] },
  adsItems = [],
  tendenciasVeredictosTitulo = null,
  veredictosTitulo = null,
  marcas = [],
  config = {},
  umbrales = {},
} = {}) {
  // El loader del agente entrega `publicacionesTendencias` (pubs canónicas
  // RAW); `publicaciones` queda como alias para la vista/tests.
  const pubs = (Array.isArray(publicacionesTendencias) && publicacionesTendencias.length > 0)
    ? publicacionesTendencias
    : (publicaciones || [])
  const veredictos = veredictosTitulo || tendenciasVeredictosTitulo || []

  // ── Curación manual: keywords excluidas (empresa.tendenciasConfig) ──
  const excluidas = new Set((config?.tendenciasConfig?.keywordsExcluidas || []).map(norm).filter(Boolean))
  let kwExcluidasHits = 0
  const filtrarKws = (kws) => (kws || []).filter(kw => {
    const fuera = excluidas.has(norm(kw?.keyword))
    if (fuera) kwExcluidasHits++
    return !fuera
  })
  const tpc = {}
  for (const [cat, data] of Object.entries(tendenciasPorCategoria || {})) {
    tpc[cat] = { ...data, keywords: filtrarKws(data?.keywords) }
  }
  const pais = { ...(tendenciasPais || {}), keywords: filtrarKws(tendenciasPais?.keywords) }

  // ── Motor base: el MISMO del dashboard, sobre el universo curado ──
  const base = clasificarTendencias({ publicaciones: pubs, tendenciasPorCategoria: tpc, tendenciasPais: pais, adsItems, umbrales })

  // ── Marcas propias (permitidas sin IA) + veredictos por par ──
  const marcasPropias = (marcas || [])
    .map(m => norm(typeof m === 'string' ? m : m?.nombre))
    .filter(t => t.length >= 3)
  const esMarcaPropia = (kw) => { const k = norm(kw); return marcasPropias.some(m => k.includes(m)) }
  const vMap = new Map()
  for (const v of veredictos) {
    const kn = v?.kwNorm || norm(v?.keyword)
    if (v?.itemId && kn) vMap.set(`${v.itemId}__${kn}`, v)
  }

  const mkItem = (row, motivo, extraMetricas = {}) => ({
    _itemId: String(row._itemId),
    title: row._titulo,
    _titulo: row._titulo,
    permalink: row._url || null,
    _motivo: motivo,
    metricas: {
      keyword: extraMetricas.keyword ?? row._kwMatch ?? null,
      categoryId: row._categoryId || null,
      tier: row._tier || null,
      vendidas30d: row._vendidas ?? null,
      stock: row._stock ?? null,
      ...extraMetricas,
    },
  })

  const secciones = { reponer_tendencia: [], buybox_tendencia: [], titulo_amplia: [], titulo_sin: [] }
  const pendientesIA = []   // pares {itemId, titulo, keyword, tier, categoria} sin veredicto
  let vetadasIA = 0
  let marcaPropiaAuto = 0

  // 1) EXACTAS — sin gate (el título ya contiene la búsqueda). Reponer
  //    ordenado por vendidas DESC: las que más demanda pierden, primero.
  for (const row of [...base.secciones.exactaSinStock].sort((a, b) => (b._vendidas || 0) - (a._vendidas || 0))) {
    secciones.reponer_tendencia.push(mkItem(row,
      `🔥 "${row._kwMatch}" está en tendencia en tu rubro y la publicación está SIN stock — reponé para captar esa demanda`))
  }
  // Buy-box, como su hermana reponer: las que más venden, primero.
  for (const row of [...base.secciones.exactaBuyBox].sort((a, b) => (b._vendidas || 0) - (a._vendidas || 0))) {
    secciones.buybox_tendencia.push(mkItem(row,
      `🔥 "${row._kwMatch}" está en tendencia y estás PERDIENDO el buy-box — revisá precio y palancas del ganador`))
  }

  // 2) TÍTULO (amplia / sin) — gate de compatibilidad por par:
  const gate = (row, keyword) => {
    if (!keyword) return { estado: 'sin-keyword' }
    if (esMarcaPropia(keyword)) { marcaPropiaAuto++; return { estado: 'compatible', motivoIA: 'marca propia del catálogo', titulosIA: [] } }
    const v = vMap.get(`${row._itemId}__${norm(keyword)}`)
    if (!v || v.aplica == null) {
      pendientesIA.push({ itemId: String(row._itemId), titulo: row._titulo, keyword, tier: row._tier === 'sin' ? 'sin' : 'amplia', categoria: row._categoryId || null })
      return { estado: 'pendiente' }
    }
    if (v.aplica === false) { vetadasIA++; return { estado: 'vetada', motivoIA: v.motivo || '' } }
    return { estado: 'compatible', motivoIA: v.motivo || '', titulosIA: Array.isArray(v.titulos) ? v.titulos : [] }
  }

  // Título: sin ventas por definición (no ordenan las vendidas), así que manda
  // el nivel de la keyword que comparte y, después, el precio.
  for (const row of [...base.secciones.ampliaOptimizar].sort(porNivelYPrecio)) {
    const g = gate(row, row._kwMatch)
    if (g.estado !== 'compatible') continue
    secciones.titulo_amplia.push(mkItem(row,
      `✏️ Meté "${row._kwMatch}" en el título — ya vendés algo relacionado (amplia → exacta) · ✓ verificado: la keyword aplica al producto`,
      { verificadoIA: g.motivoIA || null, titulosSugeridos: g.titulosIA?.length ? g.titulosIA : null }))
  }

  // La sugerida es siempre la primera keyword de su categoría (las excluidas ya
  // se filtraron antes de la base): todas son de nivel alto, así que ordena el precio.
  for (const row of [...base.secciones.sinConcordancia].sort((a, b) => (b._precio || 0) - (a._precio || 0))) {
    const sugeridas = row._sugeridas || []
    const kw = sugeridas[0] || null
    if (!kw) continue
    const g = gate(row, kw)
    if (g.estado !== 'compatible') continue
    secciones.titulo_sin.push(mkItem(row,
      `✏️ El título no agarra ninguna búsqueda del rubro — sumá "${kw}" · ✓ verificado: la keyword aplica al producto`,
      { keyword: kw, sugeridas, verificadoIA: g.motivoIA || null, titulosSugeridos: g.titulosIA?.length ? g.titulosIA : null }))
  }

  return {
    secciones,
    excluidos: {
      pendientesIA,                              // pares sin veredicto → "Verificar con IA"
      pendientesIACount: pendientesIA.length,
      vetadasIA,                                 // aplica=false — marca ajena / atributo que no aplica
      marcaPropiaAuto,                           // compatibles sin IA (marcas del catálogo)
      keywordsExcluidas: excluidas.size,         // curación manual configurada
      keywordsExcluidasHits: kwExcluidasHits,
      gapDeProducto: base.gapDeProducto.length,  // declarado — sourcing, no tarea
      categoriasCalientes: base.analisis.categoriasCalientes,
      keywordsEnTendencia: base.analisis.keywordsEnTendencia,
    },
    _base: base,
  }
}

